import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useCartSync } from "@/hooks/useCartSync";
import CartDrawer from "@/components/cart/CartDrawer";
import WhatsAppButton from "@/components/WhatsAppButton";
import Layout from "@/components/layout/Layout";
import Index from "./pages/Index";
import ScrollToTop from "./components/ScrollToTop";

const ProductPage = lazy(() => import("./pages/ProductPage"));
const QuizPage = lazy(() => import("./pages/QuizPage"));
const JournalPage = lazy(() => import("./pages/JournalPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const SciencePage = lazy(() => import("./pages/SciencePage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const ProtocolsPage = lazy(() => import("./pages/ProtocolsPage"));
const ClinicalLuxuryArticle = lazy(() => import("./pages/ClinicalLuxuryArticle"));
const BetaGlucanosArticle = lazy(() => import("./pages/BetaGlucanosArticle"));
const NgfLionsManeArticle = lazy(() => import("./pages/NgfLionsManeArticle"));
const ReishiSuenoArticle = lazy(() => import("./pages/ReishiSuenoArticle"));
const CordycepsAtpArticle = lazy(() => import("./pages/CordycepsAtpArticle"));
const ReishiCortisolArticle = lazy(() => import("./pages/ReishiCortisolArticle"));
const CortisolAdaptogenosArticle = lazy(() => import("./pages/CortisolAdaptogenosArticle"));
const AdelgazarCortisolArticle = lazy(() => import("./pages/AdelgazarCortisolArticle"));
const StackingHongosArticle = lazy(() => import("./pages/StackingHongosArticle"));
const GutBrainAxisArticle = lazy(() => import("./pages/GutBrainAxisArticle"));
const CordycepsSexualArticle = lazy(() => import("./pages/CordycepsSexualArticle"));
const AdaptogenosVsNootropicosArticle = lazy(() => import("./pages/AdaptogenosVsNootropicosArticle"));
const ReishiLongevidadArticle = lazy(() => import("./pages/ReishiLongevidadArticle"));
const OriginVideoPage = lazy(() => import("./pages/OriginVideoPage"));
const BioavailabilityVideoPage = lazy(() => import("./pages/BioavailabilityVideoPage"));
const ProtocolVideoPage = lazy(() => import("./pages/ProtocolVideoPage"));
const ShippingReturnsPage = lazy(() => import("./pages/ShippingReturnsPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const GarantiaPage = lazy(() => import("./pages/GarantiaPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const LionsManePuroLanding = lazy(() => import("./pages/LionsManePuroLanding"));
const ReishiPuroLanding = lazy(() => import("./pages/ReishiPuroLanding"));

const queryClient = new QueryClient();

const AppContent = () => {
  useCartSync();
  return (
    <>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <CartDrawer />
        <WhatsAppButton />
        <ScrollToTop />
        <Suspense fallback={null}>
          <Routes>
            {/* Quiz has its own full-screen layout */}
            <Route path="/quiz" element={<QuizPage />} />

            {/* Campaign landing — no global Layout */}
            <Route path="/lp/lions-mane-puro" element={<LionsManePuroLanding />} />
            <Route path="/lp/reishi-puro" element={<ReishiPuroLanding />} />

            {/* All other routes use shared Layout */}
            <Route path="*" element={
              <Layout>
                <Suspense fallback={null}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/producto/lions-mane-complex" element={<Navigate to="/producto/lions-mane-puro" replace />} />
                    <Route path="/producto/cordyceps-elite" element={<Navigate to="/producto/cordyceps" replace />} />
                    <Route path="/producto/reishi-balance" element={<Navigate to="/producto/reishi-puro" replace />} />
                    <Route path="/producto/:slug" element={<ProductPage />} />
                    <Route path="/journal" element={<JournalPage />} />
                    <Route path="/quienes-somos" element={<AboutPage />} />
                    <Route path="/la-ciencia" element={<SciencePage />} />
                    <Route path="/contacto" element={<ContactPage />} />
                    <Route path="/stacks" element={<ProtocolsPage />} />
                    <Route path="/tienda/protocolos" element={<ProtocolsPage />} />
                    <Route path="/journal/clinical-luxury-suplementacion" element={<ClinicalLuxuryArticle />} />
                    <Route path="/journal/beta-glucanos-sistema-inmune" element={<BetaGlucanosArticle />} />
                    <Route path="/journal/ngf-lions-mane-neurogenesis" element={<NgfLionsManeArticle />} />
                    <Route path="/journal/reishi-sueno-profundo" element={<ReishiSuenoArticle />} />
                    <Route path="/journal/cordyceps-rendimiento-atletico" element={<CordycepsAtpArticle />} />
                    <Route path="/journal/reishi-cortisol-regulacion" element={<ReishiCortisolArticle />} />
                    <Route path="/journal/cortisol-regulacion-adaptogenos" element={<CortisolAdaptogenosArticle />} />
                    <Route path="/journal/adelgazar-cortisol-adaptogenos" element={<AdelgazarCortisolArticle />} />
                    <Route path="/journal/stacking-hongos-funcionales" element={<StackingHongosArticle />} />
                    <Route path="/journal/eje-intestino-cerebro-hongos" element={<GutBrainAxisArticle />} />
                    <Route path="/journal/cordyceps-rendimiento-sexual" element={<CordycepsSexualArticle />} />
                    <Route path="/journal/adaptogenos-vs-nootropicos" element={<AdaptogenosVsNootropicosArticle />} />
                    <Route path="/journal/reishi-longevidad-telomeros" element={<ReishiLongevidadArticle />} />
                    <Route path="/the-origin" element={<OriginVideoPage />} />
                    <Route path="/the-bioavailability" element={<BioavailabilityVideoPage />} />
                    <Route path="/the-protocol" element={<ProtocolVideoPage />} />
                    <Route path="/envios-y-devoluciones" element={<ShippingReturnsPage />} />
                    <Route path="/preguntas-frecuentes" element={<FAQPage />} />
                    <Route path="/aviso-de-privacidad" element={<PrivacyPage />} />
                    <Route path="/terminos-y-condiciones" element={<TermsPage />} />
                    <Route path="/garantia" element={<GarantiaPage />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </Layout>
            } />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AppContent />
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
